import { Doc } from '@dadohr/shared/domainTypes/document';
import { EMPTY_ARR } from '@dadohr/shared/domainTypes/emptyConstants';
import {
  Event,
  EventType,
  ExperienceEvent,
  ExperienceEventMessage,
  isExperienceEventDoc
} from '@dadohr/shared/domainTypes/event';
import {
  EnrollSourceType,
  ExperienceAnchorType,
  ExperienceEventType,
  getDenormalizedSubTasks,
  IAutomationDenormalized,
  IExperience,
  IExperienceMessageDenormalized,
  IExperienceTemplate,
  ITaskDenormalized
} from '@dadohr/shared/domainTypes/experience';
import { IOrganization } from '@dadohr/shared/domainTypes/org';
import { FS } from '@dadohr/shared/versions';
import styled from '@emotion/styled';
import {
  compact,
  flatten,
  groupBy,
  keyBy,
  mapValues,
  orderBy,
  sortBy,
  uniq,
  uniqBy
} from 'lodash';
import { Undo2 } from 'lucide-react';
import React, { useMemo, useRef, useState } from 'react';
import {
  ChipFilterWithDropdown,
  ChipFilterWithSelect
} from '../../../../components/ChipFilterWithDropdown';
import { FilterByActorChip } from '../../../../components/Filters';
import { Loader } from '../../../../components/Loader';
import { LogItem, LogTable } from '../../../../components/LogTable';
import { TableSearch } from '../../../../components/Table';
import { useErrorLogger } from '../../../../hooks/useErrorLogger';
import { UnsafeTFunction, useUnsafeTranslation } from '../../../../i18next';
import { useRoutes } from '../../../../routes';
import { useInstanceEvents } from '../../../../services/experiences/instance';
import { getActorSortKey } from '../../../../services/experiences/template';
import { useMappedLoadingValue } from '../../../../services/firebase';
import { toMoment } from '../../../../services/time';
import { useUsersById } from '../../../../services/user';
import {
  NowDivider,
  useElementHeight,
  useUpcomingScheduleLogItems
} from '../../pages/Details/ParticipantDialogComponents/UpcomingScheduleList';
import { ActorDescriptor, useActorDescriptorsFromTemplate } from '../Actors';
import { AnchorField, useTemplateAnchorField } from '../Anchors';
import {
  useActionDefinitions
} from '../Builders/AutomationBuilder/actionHelpers';
import {
  FilterButton,
  SearchInput,
  SortButton,
  TextLinkButton
} from '../Builders/newux/kit/controls';
import { getFilterButtonState } from '../Builders/newux/kit/filterButtonState';
import { tokens as kitTokens } from '../Builders/newux/kit/tokens';
import { ALL_EVENT_TYPE_FILTERS, applyLogFilters, EventTypeFilterType } from './filters';
import { ExperienceEventRendererData, toLogEvent } from './toLogEvent';

const getEventTypeFilterOptions = (
  t: UnsafeTFunction,
  anchorField: AnchorField | null
): { key: EventTypeFilterType; label: string }[] =>
  compact(
    ALL_EVENT_TYPE_FILTERS.map((v) => {
      switch (v) {
        case 'SKIPPED_AND_UNSKIPPED':
          return { key: v, label: t('Skipped and unskipped') };
        case 'MOVED':
          return { key: v, label: t('Moved') };
        case 'UNCOMPLETED':
          return { key: v, label: t('Uncompleted') };
        case 'CHANGED_ANCHOR':
          if (!anchorField) {
            return null;
          }
          return {
            key: v,
            label: t('Changed {{ anchorName }}', {
              anchorName: anchorField.shortLabel(t)
            })
          };
        case 'CHANGED_SUPPORTING_ACTOR':
          return { key: v, label: t('Changed supporting actor') };
        case 'ARCHIVED':
          return { key: v, label: t('Archived') };
        case 'STARTED':
          return { key: v, label: t('Started') };
        case 'REMINDER':
          return { key: v, label: t('Reminder') };
        case 'OTHER':
          return { key: v, label: t('Other') };
        default:
          return { key: v, label: v };
      }
    })
  );

const FilterByTypeChip = ({
  value,
  onChange,
  anchorField,
  renderTrigger,
  exclusiveAll
}: {
  value: EventTypeFilterType[];
  onChange: (nextValue: EventTypeFilterType[]) => void;
  anchorField: AnchorField | null;
  renderTrigger?: (props: { onClick: () => void }) => React.ReactNode;
  exclusiveAll?: boolean;
}) => {
  const { t } = useUnsafeTranslation();
  const options = useMemo(
    () => getEventTypeFilterOptions(t, anchorField),
    [t, anchorField]
  );
  const selected = options.filter((o) => value.includes(o.key));
  return (
    <ChipFilterWithDropdown
      label={`${t('Filter by event type')}`}
      selectedOptions={selected}
      options={options}
      onChange={(xs) => onChange(xs.map((x) => x.key))}
      renderTrigger={renderTrigger}
      exclusiveAll={exclusiveAll}
    />
  );
};

const toLogKey = <T extends ExperienceEvent>(e: Doc<T>): string => {
  const d = e.data;
  const at = e.data.at.toDate().toISOString();
  const toLogKeyComponents = (): string[] => {
    switch (d.type) {
      case ExperienceEventType.EXP_ENROLL:
      case ExperienceEventType.EXP_START:
      case ExperienceEventType.EXP_DONE:
      case ExperienceEventType.EXP_ARCHIVED:
      case ExperienceEventType.ANCHOR_CHANGED:
        return [d.type, at];
      case ExperienceEventType.EXP_MESSAGE:
      case ExperienceEventType.MESSAGE_MOVED:
        return [d.type, at, d.msgId];
      case ExperienceEventType.TASK_START:
      case ExperienceEventType.TASK_SKIPPED:
      case ExperienceEventType.TASK_REMINDER:
      case ExperienceEventType.TASK_UNDONE:
      case ExperienceEventType.TASK_DONE:
      case ExperienceEventType.TASK_LATE:
      case ExperienceEventType.TASK_LATE_REMINDER:
      case ExperienceEventType.TASK_MOVED:
        return [d.type, at, d.tTId];
      case ExperienceEventType.SUB_TASK_DONE:
      case ExperienceEventType.SUB_TASK_UNDONE:
      case ExperienceEventType.SUB_TASK_SKIPPED:
        return [d.type, at, d.tTId, d.sTId];
      case ExperienceEventType.AUTOMATION_RAN:
      case ExperienceEventType.AUTOMATION_SKIPPED:
      case ExperienceEventType.AUTOMATION_UNSKIPPED:
      case ExperienceEventType.AUTOMATION_MOVED:
        return [d.type, at, d.automationTplId];
      case ExperienceEventType.AUTOMATION_ACTION_SKIPPED:
      case ExperienceEventType.AUTOMATION_ACTION_UNSKIPPED:
        return [d.type, at, d.automationTplId, d.acionTplId];
      case ExperienceEventType.SUPPORTING_ACTOR_CHANGED:
        return [d.type, at, d.actorKey];
      default:
        return [d.type, at];
    }
  };
  return toLogKeyComponents().join('-');
};

const _getLogActorKeyForTask = (
  data: ExperienceEventRendererData,
  taskTplId: string
) => {
  return data.tasksByTplId[taskTplId]?.actor || null;
};

const _getLogActorKeyForMessage = (
  data: ExperienceEventRendererData,
  msgTplId: string
) => {
  return data.messagesByTplId[msgTplId]?.actor || null;
};

const toLogActor = <T extends ExperienceEvent>(
  e: Doc<T>,
  data: ExperienceEventRendererData
): string | null => {
  const d = e.data;
  switch (d.type) {
    case ExperienceEventType.EXP_ENROLL:
    case ExperienceEventType.EXP_START:
    case ExperienceEventType.EXP_DONE:
    case ExperienceEventType.EXP_ARCHIVED:
      return 'OTHER';
    case ExperienceEventType.EXP_MESSAGE:
    case ExperienceEventType.MESSAGE_MOVED:
    case ExperienceEventType.MESSAGE_RESENT:
      return _getLogActorKeyForMessage(data, d.msgId);
    case ExperienceEventType.TASK_START:
    case ExperienceEventType.SUB_TASK_DONE:
    case ExperienceEventType.SUB_TASK_UNDONE:
    case ExperienceEventType.TASK_SKIPPED:
    case ExperienceEventType.SUB_TASK_SKIPPED:
    case ExperienceEventType.TASK_REMINDER:
    case ExperienceEventType.TASK_LATE_REMINDER:
    case ExperienceEventType.TASK_UNDONE:
    case ExperienceEventType.TASK_DONE:
    case ExperienceEventType.TASK_LATE:
    case ExperienceEventType.TASK_MOVED:
    case ExperienceEventType.TASK_RESENT:
      return _getLogActorKeyForTask(data, d.tTId);
    case ExperienceEventType.AUTOMATION_RAN:
    case ExperienceEventType.AUTOMATION_SKIPPED:
    case ExperienceEventType.AUTOMATION_UNSKIPPED:
    case ExperienceEventType.AUTOMATION_ACTION_SKIPPED:
    case ExperienceEventType.AUTOMATION_ACTION_UNSKIPPED:
      return 'AUTOMATION';
    default:
      return 'OTHER';
  }
};

const getLogExecutor = <T extends ExperienceEvent>(
  e: Doc<T>
): LogItem['executor'] => {
  // Maybe could be simplified a bit? If the channel e.g. is
  // ADMIN_APP we know the initiator is an admin user
  const d = e.data;
  switch (d.type) {
    case ExperienceEventType.EXP_ENROLL:
      if (d.source.type === EnrollSourceType.MANUAL) {
        return { type: 'user', userId: d.source.userId };
      }
      // This is handled inside the event renderer itself.
      // if (d.source.type === EnrollSourceType.IMPORT) {
      //   return { type: 'import', importId: d.source.importId };
      // }
      return null;
    case ExperienceEventType.EXP_START:
    case ExperienceEventType.EXP_MESSAGE:
    case ExperienceEventType.TASK_START:
    case ExperienceEventType.SUB_TASK_DONE:
    case ExperienceEventType.AUTOMATION_RAN:
    case ExperienceEventType.TASK_REMINDER:
    case ExperienceEventType.TASK_LATE_REMINDER:
    case ExperienceEventType.TASK_UNDONE:
    case ExperienceEventType.TASK_DONE:
    case ExperienceEventType.TASK_LATE:
    case ExperienceEventType.EXP_DONE:
      return null;
    case ExperienceEventType.TASK_RESENT:
    case ExperienceEventType.TASK_SKIPPED:
    case ExperienceEventType.TASK_UNSKIPPED:
    case ExperienceEventType.TASK_MOVED:
    case ExperienceEventType.SUB_TASK_UNDONE:
    case ExperienceEventType.SUB_TASK_SKIPPED:
    case ExperienceEventType.SUB_TASK_UNSKIPPED:
    case ExperienceEventType.AUTOMATION_SKIPPED:
    case ExperienceEventType.AUTOMATION_UNSKIPPED:
    case ExperienceEventType.AUTOMATION_ACTION_SKIPPED:
    case ExperienceEventType.AUTOMATION_ACTION_UNSKIPPED:
    case ExperienceEventType.AUTOMATION_MOVED:
    case ExperienceEventType.MESSAGE_RESENT:
    case ExperienceEventType.MESSAGE_SKIPPED:
    case ExperienceEventType.MESSAGE_UNSKIPPED:
    case ExperienceEventType.MESSAGE_MOVED:
    case ExperienceEventType.DOCUSIGN_RESTARTED:
      return { type: 'user', userId: d.initiator };
    case ExperienceEventType.EXP_ARCHIVED:
      if (d.initiator === 'automation') {
        return null;
      }
      return { type: 'user', userId: d.initiator };
    case ExperienceEventType.SUPPORTING_ACTOR_CHANGED:
      // A template edit is credited to the admin who saved the template;
      // older events, or writers that don't record them, have no initiator.
      if (d.executedBy === 'template') {
        return d.initiator ? { type: 'user', userId: d.initiator } : null;
      }
      return d.executedBy === 'user'
        ? { type: 'user', userId: d.initiator }
        : d.executedBy === 'import'
        ? { type: 'import', importId: d.initiator }
        : null;
    case ExperienceEventType.ANCHOR_CHANGED:
      return d.executedBy === 'user'
        ? { type: 'user', userId: d.initiator }
        : d.executedBy === 'import'
        ? { type: 'import', importId: d.initiator }
        : null;
    default:
      return null;
  }
};

const ToolbarRow = styled('div')({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center'
});

const Toolbar = styled('div')((p) => ({
  display: 'flex',
  justifyContent: 'flex-end',
  alignItems: 'center',
  gap: p.theme.spacing()
}));

const LogTitle = styled('div')`
  font-size: ${kitTokens.font.variant.h5.fontSize}px;
  line-height: ${kitTokens.font.variant.h5.lineHeight}px;
  font-weight: ${kitTokens.font.variant.h5.fontWeight};
  color: ${kitTokens.color.textPrimary};
  margin-bottom: ${kitTokens.space(4)};
`;

/** Opt-in via the stickyHeader prop — off by default. */
const StickyLogHeader = styled('div')`
  position: sticky;
  top: 0;
  z-index: 2;
  background: ${kitTokens.color.white};
  border-top: 1px solid ${kitTokens.color.borderSubtle};
  padding: ${kitTokens.space(6)} ${kitTokens.space(6)} ${kitTokens.space(4)};
  margin-bottom: ${kitTokens.space(2)};
`;

/** Opt-in (stickyHeader prop) so classic call sites, which render LogTable/Loader edge-to-edge, are unaffected. */
const LogContent = styled('div')`
  padding: 0 ${kitTokens.space(6)} ${kitTokens.space(6)};
`;

const EmptyText = styled('div')`
  font-size: ${kitTokens.font.variant.caption.fontSize}px;
  color: ${kitTokens.color.textSecondary};
`;

const EmptyFilteredRow = styled('div')`
  display: flex;
  align-items: center;
  gap: ${kitTokens.space(2)};
  font-size: ${kitTokens.font.variant.caption.fontSize}px;
  color: ${kitTokens.color.textSecondary};
`;

const ResetFiltersInlineButton = styled('button')`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  border: 0;
  background: none;
  cursor: pointer;
  padding: 0;
  font-family: ebony, sans-serif;
  font-size: ${kitTokens.font.variant.caption.fontSize}px;
  font-weight: ${kitTokens.font.weightScale.regular};
  color: ${kitTokens.color.blue500};
  & svg {
    width: 14px;
    height: 14px;
  }
  &:hover {
    text-decoration: underline;
  }
`;

const EVENT_ORDER: ExperienceEventType[] = [
  ExperienceEventType.EXP_ENROLL,
  ExperienceEventType.EXP_START,
  ExperienceEventType.EXP_MESSAGE,
  ExperienceEventType.TASK_START,
  ExperienceEventType.TASK_LATE,
  ExperienceEventType.TASK_REMINDER,
  ExperienceEventType.TASK_LATE_REMINDER,
  ExperienceEventType.TASK_LATE_REMINDER_MANUAL,

  ExperienceEventType.SUB_TASK_DONE,
  ExperienceEventType.SUB_TASK_UNDONE,
  ExperienceEventType.SUB_TASK_SKIPPED,
  ExperienceEventType.SUB_TASK_UNSKIPPED,

  ExperienceEventType.AUTOMATION_SKIPPED,
  ExperienceEventType.AUTOMATION_UNSKIPPED,
  ExperienceEventType.AUTOMATION_ACTION_SKIPPED,
  ExperienceEventType.AUTOMATION_ACTION_UNSKIPPED,

  ExperienceEventType.TASK_UNDONE,
  ExperienceEventType.TASK_DONE,
  ExperienceEventType.EXP_DONE
];

const getEventTypeSortKey = (d: Doc<Event>) => {
  if (isExperienceEventDoc(d)) {
    const i = EVENT_ORDER.indexOf(d.data.type);
    if (i !== -1) {
      return i;
    }
  }
  return Infinity;
};

const TYPES_REQUIRING_MINUTE_PRECISION: EventType[] = [
  ExperienceEventType.EXP_ENROLL,
  ExperienceEventType.EXP_START,
  ExperienceEventType.EXP_MESSAGE,
  ExperienceEventType.TASK_START,
  ExperienceEventType.TASK_REMINDER,
  ExperienceEventType.TASK_LATE_REMINDER,
  ExperienceEventType.TASK_LATE_REMINDER_MANUAL
];

export const ExperienceLog = ({
  org,
  tpl,
  d,
  tasks,
  messages,
  automations,
  anchorFields,
  toolbarLeftContent,
  title,
  stickyHeader,
  headerRef,
  dividerRef
}: {
  org: IOrganization;
  tpl: Doc<IExperienceTemplate>;
  d: Doc<IExperience>;
  tasks: ITaskDenormalized[];
  messages: IExperienceMessageDenormalized[];
  automations: IAutomationDenormalized[];
  anchorFields: AnchorField[];
  /** Rendered left-aligned on the same row as the sort/filter/search controls. */
  toolbarLeftContent?: React.ReactNode;
  /** Rendered above the toolbar, inside the same sticky header when stickyHeader is set. */
  title?: React.ReactNode;
  /** Opt-in — classic call sites leave this unset and keep the default non-sticky layout. */
  stickyHeader?: boolean;
  /** Exposes the sticky header's element so callers can measure its height (e.g. to offset scroll-to targets below it). Only attached when stickyHeader is set. */
  headerRef?: React.RefObject<HTMLDivElement>;
  /** Scroll target for the "Now" divider between history and pending-schedule rows. Only rendered when stickyHeader is set. */
  dividerRef?: React.RefObject<HTMLDivElement>;
}) => {
  const { t } = useUnsafeTranslation();
  const [events, loading, error] = useInstanceEvents(d.data.orgId, d.id);
  useErrorLogger('experience log', error);
  const { ROUTES } = useRoutes();
  const anchorField = useTemplateAnchorField(tpl, org.customUserFields);
  const actionDefinitions = useActionDefinitions();
  const {
    tasksByTplId,
    subTasksByTplId,
    automationsByTplId,
    automationActionsByTplId,
    messagesByTplId
  } = useMemo(() => {
    return {
      tasksByTplId: keyBy(tasks, (task) => task.templateId),
      subTasksByTplId: keyBy(
        flatten(tasks.map(getDenormalizedSubTasks)),
        (s) => s.templateId
      ),
      automationActionsByTplId: keyBy(
        flatten(automations.map((a) => a.actions)),
        (ac) => ac.templateId
      ),
      automationsByTplId: keyBy(automations, (a) => a.templateId),
      messagesByTplId: keyBy(messages, (m) => m.templateId)
    };
  }, [tasks, automations, messages]);
  const [sort, setSort] = useState<'newestFirst' | 'oldestFirst'>(
    'oldestFirst'
  );

  const userIds = useMemo(() => {
    if (!events) {
      return EMPTY_ARR;
    }
    return uniq(
      compact(
        flatten(
          events.map((ev) => {
            if (isExperienceEventDoc(ev)) {
              if (
                ev.data.type === ExperienceEventType.SUPPORTING_ACTOR_CHANGED
              ) {
                return [ev.data.prev, ev.data.next];
              }
            }
            return null;
          })
        )
      )
    );
  }, [events]);

  const [usersById] = useMappedLoadingValue(useUsersById(userIds), (users) =>
    keyBy(compact(users), (u) => u.id)
  );

  const anchor = useMemo(() => {
    const a = tpl.data.anchor;
    if (a.type === ExperienceAnchorType.EMPLOYEE_DATE) {
      return anchorFields.find((f) => f.field === a.field) || null;
    }
    return null;
  }, [tpl, anchorFields]);

  const messageEvents = useMemo(() => {
    const msgs = mapValues(
      groupBy(
        d.data.messages.filter(
          (x) =>
            x.type === 'EXPERIENCE_MESSAGE' ||
            x.type === 'EXPERIENCE_MESSAGE_PER_COHORT_AGGREGATED' ||
            x.type === 'EXPERIENCE_MESSAGE_PER_COHORT_UNNESTED'
        ),
        (m) => m.scope?.msgId || ''
      ),
      (ms) => sortBy(ms, (m) => m.at.toMillis())[0]
    );
    return compact(
      messages.map<Doc<ExperienceEventMessage> | null>((m) => {
        const cm = msgs[m.templateId];
        if (!cm) {
          return null;
        }
        return {
          id: m.templateId, // something more precise might be useful here later, especially for comments
          collection: FS.events,
          data: {
            orgId: d.data.orgId,
            kind: 'EXPERIENCE',
            type: ExperienceEventType.EXP_MESSAGE,
            at: cm.at,
            initiator: '',
            expId: d.id,
            expTId: d.data.templateId,
            msgId: m.templateId
          }
        };
      })
    );
  }, [messages, d.data.messages, d.id, d.data.templateId, d.data.orgId]);

  const uniqEvents = useMemo(() => {
    if (!events) {
      return null;
    }
    return uniqBy([...events, ...messageEvents], (ev) =>
      [
        ev.data.type,
        ev.data.at.toDate().toISOString(),
        ...(isExperienceEventDoc(ev) ? toLogKey(ev) : '')
      ].join('-')
    );
  }, [events, messageEvents]);

  const actorDescriptors = useActorDescriptorsFromTemplate(
    tpl.data.supportingActors
  );

  const [filters, setFilters] = useState<{
    q: string;
    actorKeys: (string | 'AUTOMATION' | 'OTHER')[];
    types: EventTypeFilterType[];
  }>({
    q: '',
    // The new-UX Actor/Type filter buttons use "All" to mean "nothing individually
    // selected" (see exclusiveAll on ChipFilterWithDropdown), so they start empty;
    // the classic dropdown's "All" instead means "every option individually ticked",
    // so it starts fully selected.
    actorKeys: stickyHeader
      ? []
      : [...Object.keys(actorDescriptors), 'AUTOMATION', 'OTHER'],
    types: stickyHeader ? [] : ALL_EVENT_TYPE_FILTERS
  });

  const resourcesById = useMemo(
    () => keyBy(tpl.data.resources, (r) => r.resourceId),
    [tpl.data.resources]
  );

  const logItems = useMemo(() => {
    if (!uniqEvents || !usersById) {
      return null;
    }
    const rendererData: ExperienceEventRendererData = {
      tasksByTplId,
      subTasksByTplId,
      automationsByTplId,
      automationActionsByTplId,
      messagesByTplId,
      actorDescriptors,
      usersById,
      dateFormats: org.dateFormats,
      tz: stickyHeader ? tpl.data.tz : undefined,
      anchor,
      automationActionDefinitions: actionDefinitions,
      resourcesById,
      routes: ROUTES
    };
    // Sent message
    // Started Task --> Event is collected BEFORE e.g. an email is sent out ---> but only through that i get the Message SENT event
    //
    //
    // Received message
    // Started task
    // started task

    //

    const sorted = orderBy(uniqEvents, [
      (ev) =>
        // startOf('minute') is a hack to the problem of batches
        // TASK_START events are recorded earlier than the actually sent message
        // Furthermore they might also not lead to nice actor groupings.
        // So let's get everything down to a minute granularity and have the rest figured
        // out by by actor and type sortings
        (TYPES_REQUIRING_MINUTE_PRECISION.includes(ev.data.type)
          ? toMoment(ev.data.at).startOf('minute').valueOf()
          : ev.data.at.toMillis()) * (sort === 'newestFirst' ? -1 : 1),
      (ev) => {
        const actor = isExperienceEventDoc(ev)
          ? toLogActor(ev, rendererData)
          : null;
        if (!actor) {
          return '000000';
        }
        return actor === 'OTHER'
          ? '000000'
          : getActorSortKey(actor === 'AUTOMATION' ? null : actor);
      },
      (ev) => getEventTypeSortKey(ev)
    ]);
    return compact(
      sorted.map<LogItem | null>((ev) => {
        if (!isExperienceEventDoc(ev)) {
          return null;
        }
        const eventD = toLogEvent(ev, rendererData, t);
        if (!eventD) {
          return null;
        }
        return {
          key: ev.id,
          at: ev.data.at,
          d: eventD.node,
          searchValue: eventD.searchValue,
          executor: getLogExecutor(ev),
          actor: toLogActor(ev, rendererData),
          type: ev.data.type
        };
      })
    );
  }, [
    uniqEvents,
    tasksByTplId,
    subTasksByTplId,
    automationsByTplId,
    automationActionsByTplId,
    messagesByTplId,
    sort,
    actorDescriptors,
    usersById,
    org,
    anchor,
    actionDefinitions,
    resourcesById,
    t,
    stickyHeader,
    tpl
  ]);

  const filteredLogItems = useMemo(
    () => applyLogFilters(logItems, filters),
    [filters, logItems]
  );

  const upcomingLogItems = useUpcomingScheduleLogItems(
    tpl,
    d,
    actorDescriptors,
    t,
    !!stickyHeader
  );
  const filteredUpcomingLogItems = useMemo(
    () => applyLogFilters(upcomingLogItems, filters),
    [filters, upcomingLogItems]
  );
  // useUpcomingScheduleLogItems always returns soonest-first; reverse to match a
  // "newest/latest first" sort, so the two blocks (history + upcoming) read in a
  // single consistent direction on either side of the "Now" divider.
  const orderedUpcomingLogItems = useMemo(() => {
    if (!filteredUpcomingLogItems) return filteredUpcomingLogItems;
    return sort === 'newestFirst'
      ? [...filteredUpcomingLogItems].reverse()
      : filteredUpcomingLogItems;
  }, [filteredUpcomingLogItems, sort]);
  const fallbackHeaderRef = useRef<HTMLDivElement>(null);
  const headerHeight = useElementHeight(headerRef || fallbackHeaderRef);

  const actorFilterDescriptors: { [actorKey: string]: ActorDescriptor } = {
    ...actorDescriptors,
    AUTOMATION: {
      color: '',
      key: 'AUTOMATION',
      label: t('Automation'),
      type: 'AUTOMATION'
    },
    OTHER: {
      color: '',
      key: 'OTHER',
      label: t('Other'),
      type: 'OTHER'
    }
  };
  const actorOptions = Object.values(actorFilterDescriptors).map((a) => ({
    key: a.key,
    label: a.label
  }));
  const { active: actorFilterActive, label: actorButtonLabel } = getFilterButtonState(
    actorOptions,
    filters.actorKeys,
    t('Actor'),
    (count) => t('{{count}} actors', { count })
  );

  const typeFilterOptions = getEventTypeFilterOptions(t, anchorField);
  const { active: typeFilterActive, label: typeButtonLabel } = getFilterButtonState(
    typeFilterOptions,
    filters.types,
    t('Event type'),
    (count) => t('{{count}} types', { count })
  );

  const anyFilterActive = actorFilterActive || typeFilterActive;
  const resetAllFilters = () =>
    setFilters((x) => ({ ...x, actorKeys: [], types: [] }));

  const anyFilterOrSearchApplied = anyFilterActive || !!filters.q;
  const resetAllFiltersAndSearch = () =>
    setFilters((x) => ({ ...x, actorKeys: [], types: [], q: '' }));

  const emptyFilteredState = (
    <EmptyFilteredRow>
      {t('Nothing matching these filters')}
      <ResetFiltersInlineButton type="button" onClick={resetAllFiltersAndSearch}>
        <Undo2 /> {t('Reset filters')}
      </ResetFiltersInlineButton>
    </EmptyFilteredRow>
  );

  const toolbar = (
    <Toolbar>
      {!stickyHeader && (
        <ChipFilterWithSelect
          value={sort}
          onChange={(nextSort) => setSort(nextSort)}
          options={[
            {
              value: 'newestFirst',
              label: t('Newest first')
            },

            {
              value: 'oldestFirst',
              label: t('Oldest first')
            }
          ]}
        />
      )}
      {stickyHeader && anyFilterActive && (
        <TextLinkButton label={t('Reset all filters')} onClick={resetAllFilters} />
      )}
        <FilterByActorChip
          value={filters.actorKeys}
          onChange={(nextActorKeys) =>
            setFilters((x) => ({ ...x, actorKeys: nextActorKeys }))
          }
          actorDescriptors={actorFilterDescriptors}
          renderTrigger={
            stickyHeader
              ? ({ onClick }) => (
                  <FilterButton
                    label={actorButtonLabel}
                    active={actorFilterActive}
                    onClick={onClick}
                  />
                )
              : undefined
          }
          exclusiveAll={stickyHeader}
        />
        <FilterByTypeChip
          anchorField={anchorField}
          value={filters.types}
          onChange={(nextTypes) =>
            setFilters((x) => ({ ...x, types: nextTypes }))
          }
          renderTrigger={
            stickyHeader
              ? ({ onClick }) => (
                  <FilterButton
                    label={typeButtonLabel}
                    active={typeFilterActive}
                    onClick={onClick}
                  />
                )
              : undefined
          }
          exclusiveAll={stickyHeader}
        />

      {stickyHeader && (
        <SortButton
          descending={sort === 'newestFirst'}
          onClick={() =>
            setSort((x) => (x === 'newestFirst' ? 'oldestFirst' : 'newestFirst'))
          }
          label={t('Date')}
        />
      )}

      {stickyHeader ? (
        <SearchInput
          value={filters.q}
          onChange={(nextQ) => setFilters((x) => ({ ...x, q: nextQ }))}
          autoFocus
          placeholder={t('Search log')}
        />
      ) : (
        <TableSearch
          term={filters.q}
          onChange={(nextQ) => setFilters((x) => ({ ...x, q: nextQ }))}
          autoFocus
          placeholder={t('Search log')}
        />
      )}
    </Toolbar>
  );

  const header = (
    <>
      {title && <LogTitle>{title}</LogTitle>}
      {toolbarLeftContent ? (
        <ToolbarRow>
          {toolbarLeftContent}
          {toolbar}
        </ToolbarRow>
      ) : (
        toolbar
      )}
    </>
  );

  const historyBlock = (
    <>
      {!!filteredLogItems?.length && (
        <LogTable
          orgId={tpl.data.orgId}
          ds={filteredLogItems}
          tz={stickyHeader ? tpl.data.tz : undefined}
        />
      )}
      {stickyHeader &&
        filteredLogItems?.length === 0 &&
        (anyFilterOrSearchApplied ? (
          emptyFilteredState
        ) : (
          <EmptyText>{t('No events have occured yet')}</EmptyText>
        ))}
      {loading && <Loader height={300} />}
    </>
  );

  const upcomingBlock = stickyHeader && (
    <>
      {orderedUpcomingLogItems === undefined && <Loader height={120} />}
      {orderedUpcomingLogItems?.length === 0 &&
        (anyFilterOrSearchApplied ? (
          emptyFilteredState
        ) : (
          <EmptyText>{t('No upcoming events')}</EmptyText>
        ))}
      {!!orderedUpcomingLogItems?.length && (
        <LogTable orgId={tpl.data.orgId} ds={orderedUpcomingLogItems} tz={tpl.data.tz} />
      )}
    </>
  );

  const nowDivider = stickyHeader && (
    <div ref={dividerRef} style={{ scrollMarginTop: headerHeight }}>
      <NowDivider>{t('Now')}</NowDivider>
    </div>
  );

  // Ascending (default): oldest → newest history, then Now, then soonest → latest
  // upcoming. Descending: latest → soonest upcoming, then Now, then newest → oldest
  // history — the two blocks read in one continuous direction across the divider.
  const rows =
    sort === 'newestFirst' ? (
      <>
        {upcomingBlock}
        {nowDivider}
        {historyBlock}
      </>
    ) : (
      <>
        {historyBlock}
        {nowDivider}
        {upcomingBlock}
      </>
    );

  return (
    <>
      {stickyHeader ? (
        <StickyLogHeader ref={headerRef}>{header}</StickyLogHeader>
      ) : (
        header
      )}
      {stickyHeader ? <LogContent>{rows}</LogContent> : rows}
    </>
  );
};
